import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import { useState } from "react";
import ReactHtmlParser from "react-html-parser";

import { PRICING_PAGE_URL, TERMS_OF_USE_URL } from "../constants";
import {
  ADDITIONAL_TOOL_PRICE_EUR,
  App,
  BillingAddress,
  Plan,
} from "../types/entities";
import {
  BillingAddressDraft,
  BillingAddressForm,
} from "./billing-address-form";
import { ModalLegalFooter } from "./modal-legal-footer";

export interface ActivateToolModalState {
  isOpen: boolean;
  app?: App | null;
  isPaid: boolean;
  currentPlan?: Plan | null;
  includedToolsUsed: number;
  billingAddress?: BillingAddress | null;
  isSubmitting?: boolean;
  errorMessage?: string;
  language: SupportedLanguage;
}

export type ActivateToolModalEvent = "close-activate-modal" | "submit-activate";

export interface SubmitActivatePayload {
  appId: string;
  billingAddress: BillingAddressDraft;
}

export const ActivateToolModal: PimoReactComponent<
  ActivateToolModalState,
  ActivateToolModalEvent,
  SubmitActivatePayload
> = ({
  fireEvent,
  isOpen,
  app,
  isPaid,
  currentPlan,
  includedToolsUsed,
  billingAddress,
  isSubmitting,
  errorMessage,
  language,
}) => {
  const [agreed, setAgreed] = useState(false);
  const [billingValid, setBillingValid] = useState(false);
  const [billingDraft, setBillingDraft] = useState<BillingAddressDraft | null>(
    null
  );

  if (!app) return null;

  const canSubmit = agreed && billingValid && !isSubmitting && billingDraft;

  return (
    <Dialog
      open={isOpen}
      onClose={() => fireEvent?.("close-activate-modal")}
      slotProps={{ transition: { onEnter: () => setAgreed(false) } }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {getLocalizedMessage(language, "activateToolModal.title")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2">{app.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {getLocalizedMessage(language, `useCase.${app.useCase}`)}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: isPaid ? "#fff8e1" : "#e8f5e9",
            }}
          >
            <Typography variant="subtitle2">
              {isPaid
                ? getLocalizedMessage(
                    language,
                    "activateToolModal.pricePerMonth",
                    { price: ADDITIONAL_TOOL_PRICE_EUR }
                  )
                : getLocalizedMessage(
                    language,
                    "activateToolModal.includedInPlan"
                  )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isPaid
                ? getLocalizedMessage(
                    language,
                    "activateToolModal.billedWithPlan"
                  )
                : getLocalizedMessage(
                    language,
                    "activateToolModal.toolUsesIncludedSlot"
                  )}
            </Typography>
          </Box>

          {currentPlan ? (
            <Typography variant="body2" color="text.secondary">
              {ReactHtmlParser(
                getLocalizedMessage(
                  language,
                  "activateToolModal.currentPlanOverview",
                  {
                    plan: currentPlan.name,
                    includedUsed: includedToolsUsed,
                    includedTotal: currentPlan.includedTools,
                    userLimit: currentPlan.userLimit,
                    tool: getLocalizedMessage(
                      language,
                      `activateToolModal.${currentPlan.includedTools === 1 ? "tool" : "tools"}`
                    ),
                  }
                )
              )}
            </Typography>
          ) : null}

          <BillingAddressForm
            address={billingAddress}
            onChange={setBillingDraft}
            onValidityChange={setBillingValid}
            language={language}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                {getLocalizedMessage(
                  language,
                  "activateToolModal.activationConfirmation",
                  {
                    tool: getLocalizedMessage(
                      language,
                      `activateToolModal.${isPaid ? "paidTool" : "tool"}`
                    ),
                  }
                )}
                <Link href={TERMS_OF_USE_URL} target="_blank" rel="noopener">
                  {getLocalizedMessage(
                    language,
                    "activateToolModal.termsOfUse"
                  )}
                </Link>
                .
              </Typography>
            }
          />

          <Typography variant="body2">
            <Link
              href={PRICING_PAGE_URL}
              target="_blank"
              rel="noopener"
              underline="hover"
              sx={{ color: "primary.main", fontWeight: 500 }}
            >
              {getLocalizedMessage(language, "activateToolModal.seePricing")}
            </Link>
          </Typography>

          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 1 }}>
        <Button onClick={() => fireEvent?.("close-activate-modal")}>
          {getLocalizedMessage(language, "dialogActions.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={() => {
            if (!canSubmit || !billingDraft) return;
            fireEvent?.("submit-activate", {
              appId: app.id,
              billingAddress: billingDraft,
            });
          }}
          sx={{
            bgcolor: "#ff8c00",
            color: "#fff",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { bgcolor: "#e67e00" },
            "&.Mui-disabled": { bgcolor: "#f0d4b3", color: "#fff" },
          }}
        >
          {getLocalizedMessage(language, "dialogActions.activateTool")}
        </Button>
      </DialogActions>
      <ModalLegalFooter language={language} />
    </Dialog>
  );
};
